import {
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  type SelectChangeEvent,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import type { SearchField } from '@/api/tenant';

const FIELD_OPTIONS: { value: SearchField; label: string }[] = [
  { value: 'name',     label: 'Name' },
  { value: 'gst',      label: 'GST Number' },
  { value: 'phone',    label: 'Phone Number' },
  { value: 'location', label: 'Location' },
];

/**
 * Field-scoped search: a dropdown to pick which field to search by
 * (Name / GST Number / Phone Number / Location), a text input, and a
 * search icon button that triggers the search explicitly — no live
 * debounce, the caller decides what "search" means (click, or Enter).
 *
 * Each instance owns its own field selection. Two screens/tabs using this
 * component side by side must each pass their own `field`/`onFieldChange`
 * state — they are never meant to share state across instances.
 */
export function SearchWithFieldSelect({
  field, onFieldChange, value, onChangeText, onSearch, placeholder, sx,
}: {
  field: SearchField;
  onFieldChange: (field: SearchField) => void;
  value: string;
  onChangeText: (text: string) => void;
  onSearch: () => void;
  placeholder?: string;
  sx?: object;
}) {
  const fieldLabel = FIELD_OPTIONS.find(f => f.value === field)?.label ?? 'Name';
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={sx}>
      <Select
        size="small"
        value={field}
        onChange={(e: SelectChangeEvent) => onFieldChange(e.target.value as SearchField)}
        sx={{ minWidth: 130 }}
      >
        {FIELD_OPTIONS.map(opt => (
          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
        ))}
      </Select>
      <TextField
        size="small"
        placeholder={placeholder ?? `Search by ${fieldLabel.toLowerCase()}…`}
        value={value}
        onChange={e => onChangeText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') onSearch(); }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={onSearch} aria-label="search" edge="end">
                <SearchIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ minWidth: { xs: 200, sm: 260 } }}
      />
    </Stack>
  );
}
